import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTranslations } from '../context/TranslationsContext';
import { useFitnet } from '../contexts/FitnetContext';
import { getHomePath } from '../utils/routes';

const { CreditCard, Gift, Calendar, Check, Award, User, ChevronRight, Ticket, Star, Users, Mail, Tag, X, Loader2, PartyPopper, Info, ServerOff, Phone } = LucideIcons;

// Get icon component by name from lucide-react
const getIconByName = (iconName) => {
  return LucideIcons[iconName] || null;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PassesPage = () => {
  const { t: ti18n, i18n } = useTranslation();
  const { td } = useTranslations();
  const { user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const { isAvailable: isFitnetAvailable, loading: fitnetLoading } = useFitnet();
  
  // Combined translation function - first try CMS (td), then i18n, then fallback
  const t = (key, fallback = null) => {
    const cmsValue = td(key);
    if (cmsValue && cmsValue !== key) return cmsValue;
    // Try without prefix
    const shortKey = key.split('.').pop();
    const cmsShort = td(shortKey);
    if (cmsShort && cmsShort !== shortKey) return cmsShort;
    return fallback || key;
  };

  const [passGroups, setPassGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedPass, setSelectedPass] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const [fitnetTranslations, setFitnetTranslations] = useState({});
  
  // Purchase options
  const [purchaseMode, setPurchaseMode] = useState('self'); // 'self', 'subaccount', 'gift'
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [subaccounts, setSubaccounts] = useState([]);
  const [giftEmail, setGiftEmail] = useState('');
  const [giftEmailConfirm, setGiftEmailConfirm] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  
  // Activation option (new feature)
  const [activateImmediately, setActivateImmediately] = useState(true);  // Default to immediate
  const [activationDeadlineDays, setActivationDeadlineDays] = useState(60);
  
  // Discount code
  const [promoCode, setPromoCode] = useState('');
  const [promoCodeError, setPromoCodeError] = useState(null);
  const [promoCodeValid, setPromoCodeValid] = useState(false);
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  
  // Success modal for free pass
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successPassData, setSuccessPassData] = useState(null);
  
  // Loyalty points
  const [loyaltyPoints, setLoyaltyPoints] = useState({});
  const [loyaltyActive, setLoyaltyActive] = useState(false);

  // Normalize name to translation key
  const normalizeNameToKey = (name) => {
    if (!name) return '';
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ł/g, 'l').replace(/Ł/g, 'L')
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');
  };

  // Get translated name
  const getTranslatedName = (originalName) => {
    if (!originalName) return originalName;
    const key = normalizeNameToKey(originalName);
    if (fitnetTranslations[key]?.[lang]) {
      return fitnetTranslations[key][lang];
    }
    return originalName;
  };

  // Get translated description
  const getTranslatedDescription = (passName, originalDescription) => {
    if (!originalDescription) return null;
    const key = normalizeNameToKey(passName) + '_desc';
    if (fitnetTranslations[key]?.[lang]) {
      return fitnetTranslations[key][lang];
    }
    return originalDescription;
  };

  // Fetch translations
  const fetchTranslations = async () => {
    try {
      const response = await axios.get(`${API}/admin/fitnet-translations`);
      setFitnetTranslations(response.data);
    } catch (error) {
      console.error('Failed to fetch translations:', error);
    }
  };

  // Fetch public settings (including pass activation deadline and loyalty)
  const fetchPublicSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/public`);
      if (response.data.passActivationDeadlineDays) {
        setActivationDeadlineDays(response.data.passActivationDeadlineDays);
      }
      // Also fetch loyalty product points
      if (response.data.loyaltyProgramActive) {
        setLoyaltyActive(true);
        fetchLoyaltyPoints();
      }
    } catch (error) {
      console.error('Failed to fetch public settings:', error);
    }
  };
  
  // Fetch loyalty points for products
  const fetchLoyaltyPoints = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/product-points`);
      if (response.data.active) {
        setLoyaltyActive(true);
        setLoyaltyPoints(response.data.points || {});
      } else {
        setLoyaltyActive(false);
        setLoyaltyPoints({});
      }
    } catch (error) {
      console.error('Failed to fetch loyalty points:', error);
    }
  };
  
  // Get loyalty points for a pass
  const getPassLoyaltyPoints = (pass) => {
    if (!loyaltyActive || !pass) return 0;
    // Try normalized name key first (e.g., 'pass_Karnet_wakacyjny')
    const nameKey = `pass_${normalizeNameToKey(pass.name)}`;
    if (loyaltyPoints[nameKey]) return loyaltyPoints[nameKey];
    // Fallback to variantId or id
    const variantKey = `pass_${pass.variantId}`;
    const idKey = `pass_${pass.id}`;
    return loyaltyPoints[variantKey] || loyaltyPoints[idKey] || 0;
  };

  useEffect(() => {
    fetchPassGroups();
    fetchTranslations();
    fetchPublicSettings();
  }, []);
  
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchSubaccounts();
    }
  }, [isAuthenticated, token]);

  // Auto-select first group with passes when data loads
  useEffect(() => {
    if (passGroups.length > 0 && !selectedGroup) {
      // Find first group that has passes
      const firstWithPasses = passGroups.find(g => g.passes && g.passes.length > 0);
      if (firstWithPasses) {
        setSelectedGroup(firstWithPasses);
      }
    }
  }, [passGroups]);

  const fetchSubaccounts = async () => {
    try {
      const response = await axios.get(`${API}/subaccounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubaccounts(response.data || []);
    } catch (err) {
      console.error('Failed to fetch subaccounts:', err);
    }
  };

  const fetchPassGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch pass groups and sort orders in parallel
      const [passResponse, catSortResponse, passSortResponse] = await Promise.all([
        axios.get(`${API}/passes/grouped`),
        axios.get(`${API}/sort-order/passCategories`).catch(() => ({ data: [] })),
        axios.get(`${API}/sort-order/passes`).catch(() => ({ data: [] }))
      ]);
      
      // Create sort order maps
      const catOrderMap = new Map(catSortResponse.data.map(o => [o.itemId, o.order]));
      const passOrderMap = new Map(passSortResponse.data.map(o => [o.itemId, o.order]));
      
      // Filter out empty groups and sort them
      let groupsWithPasses = passResponse.data.filter(g => g.passes && g.passes.length > 0);
      
      // Sort groups by custom order
      groupsWithPasses.sort((a, b) => {
        const orderA = catOrderMap.get(`cat_${a.name}`) ?? catOrderMap.get(`cat_${a.id}`) ?? 999;
        const orderB = catOrderMap.get(`cat_${b.name}`) ?? catOrderMap.get(`cat_${b.id}`) ?? 999;
        return orderA - orderB;
      });
      
      // Sort passes within each group
      groupsWithPasses = groupsWithPasses.map(group => ({
        ...group,
        passes: [...group.passes].sort((a, b) => {
          const orderA = passOrderMap.get(`pass_${a.variantId}`) ?? passOrderMap.get(`pass_${a.id}`) ?? 999;
          const orderB = passOrderMap.get(`pass_${b.variantId}`) ?? passOrderMap.get(`pass_${b.id}`) ?? 999;
          return orderA - orderB;
        })
      }));
      
      setPassGroups(groupsWithPasses);
    } catch (error) {
      console.error('Failed to fetch pass groups:', error);
      setError(t('passes.loadError', 'Failed to load passes'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPass = (pass) => {
    setSelectedPass(pass);
    // Reset purchase options when selecting new pass
    setPurchaseMode(isAuthenticated ? 'self' : 'gift');
    setSelectedPerson(null);
    setGiftEmail('');
    setGiftEmailConfirm('');
    setBuyerEmail('');
    setBuyerName('');
    setBuyerPhone('');
    setError(null);
    // Reset promo code state
    setPromoCode('');
    setPromoCodeError(null);
    setPromoCodeValid(false);
    setAppliedDiscount(null);
  };
  
  // Validate promo code
  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoCodeError(t('passes.enterPromoCode', 'Enter discount code'));
      return;
    }
    
    setPromoCodeLoading(true);
    setPromoCodeError(null);
    
    try {
      const response = await axios.post(`${API}/payments/validate-discount`, {
        code: promoCode.trim(),
        passId: selectedPass.variantId || selectedPass.id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        setPromoCodeValid(true);
        setAppliedDiscount(response.data.discount);
        setPromoCodeError(null);
      } else {
        setPromoCodeValid(false);
        setAppliedDiscount(null);
        setPromoCodeError(response.data.errorPl || response.data.error || response.data.errorEn);
      }
    } catch (error) {
      console.error('Failed to validate promo code:', error);
      setPromoCodeValid(false);
      setAppliedDiscount(null);
      setPromoCodeError(t('passes.codeValidationError', 'Code validation error'));
    } finally {
      setPromoCodeLoading(false);
    }
  };
  
  // Clear promo code
  const clearPromoCode = () => {
    setPromoCode('');
    setPromoCodeValid(false);
    setPromoCodeError(null);
    setAppliedDiscount(null);
  };
  
  // Calculate discounted price
  const calculateDiscountedPrice = () => {
    if (!selectedPass || !appliedDiscount) return selectedPass?.price || 0;
    
    let discountAmount = 0;
    if (appliedDiscount.discountType === 'percentage') {
      discountAmount = Math.round((selectedPass.price * appliedDiscount.discountValue) / 100);
    } else {
      discountAmount = Math.min(appliedDiscount.discountValue, selectedPass.price);
    }
    return Math.max(0, selectedPass.price - discountAmount);
  };
  
  const openPurchaseModal = () => {
    if (!selectedPass || !selectedPass.price || selectedPass.price <= 0) {
      setError(t('passes.selectPassWithPrice', 'Select a pass with a price'));
      return;
    }
    
    // For guests, automatically set gift mode
    if (!isAuthenticated) {
      setPurchaseMode('gift');
    }
    
    setShowPurchaseModal(true);
  };

  const handlePurchase = async () => {
    if (!selectedPass || !selectedPass.price || selectedPass.price <= 0) {
      setError(t('passes.selectPassWithPrice', 'Select a pass with a price'));
      return;
    }
    
    // For authenticated users buying for self/subaccount
    if (isAuthenticated && purchaseMode !== 'gift') {
      setPurchasing(true);
      setError(null);
      
      try {
        const requestData = {
          passId: selectedPass.variantId || selectedPass.id,  // variantId is needed for FitNet ZakupKarnetu
          forUserId: purchaseMode === 'subaccount' ? selectedPerson : user.user_id,
          isGift: false,
          activateImmediately: activateImmediately,  // NEW: Include activation preference
          promoCode: promoCodeValid ? promoCode.trim() : null,  // Include promo code if validated
          lang: lang // Pass user's language for email notifications
        };
        
        const response = await axios.post(`${API}/payments/pass`, requestData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Handle 100% discount (free pass) - no redirect needed
        if (response.data.success && !response.data.redirectUri) {
          // Pass was activated for free - show success modal
          setShowPurchaseModal(false);
          setSuccessPassData({
            passName: selectedPass.name,
            passCode: response.data.passCode,
            isFree: true,
            activatedImmediately: activateImmediately
          });
          setShowSuccessModal(true);
          return;
        }
        
        if (response.data.success && response.data.redirectUri) {
          window.location.href = response.data.redirectUri;
        } else {
          setError(t('passes.paymentInitError', 'Failed to initialize payment'));
        }
      } catch (error) {
        console.error('Failed to purchase pass:', error);
        setError(error.response?.data?.errorPl || error.response?.data?.error || 
          t('passes.purchaseError', 'Pass purchase error'));
      } finally {
        setPurchasing(false);
      }
      return;
    }
    
    // Gift purchase (can be guest or authenticated)
    // Validate gift emails
    if (!giftEmail || !giftEmailConfirm) {
      setError(t('passes.enterRecipientEmail', 'Enter recipient email'));
      return;
    }
    if (giftEmail.toLowerCase() !== giftEmailConfirm.toLowerCase()) {
      setError(t('passes.emailsNotMatch', 'Recipient emails do not match'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(giftEmail)) {
      setError(t('passes.invalidRecipientEmail', 'Invalid recipient email format'));
      return;
    }
    
    // For guest gift purchases, require buyer email, name and phone
    if (!isAuthenticated) {
      if (!buyerEmail) {
        setError(t('passes.enterYourEmail', 'Enter your email'));
        return;
      }
      if (!emailRegex.test(buyerEmail)) {
        setError(t('passes.invalidYourEmail', 'Invalid your email format'));
        return;
      }
      if (!buyerName || buyerName.trim().length < 2) {
        setError(t('passes.enterYourName', 'Enter your name'));
        return;
      }
      if (!buyerPhone || buyerPhone.trim().length < 9) {
        setError(t('passes.enterYourPhone', 'Enter your phone number'));
        return;
      }
    }
    
    setPurchasing(true);
    setError(null);
    
    try {
      let response;
      
      if (isAuthenticated) {
        // Authenticated gift purchase
        const requestData = {
          passId: selectedPass.variantId || selectedPass.id,  // variantId is needed for FitNet ZakupKarnetu
          isGift: true,
          giftEmail,
          giftEmailConfirm,
          lang: lang // Pass user's language for email notifications
        };
        
        response = await axios.post(`${API}/payments/pass`, requestData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Guest gift purchase
        const requestData = {
          passId: selectedPass.variantId || selectedPass.id,  // variantId is needed for FitNet ZakupKarnetu
          giftEmail,
          giftEmailConfirm,
          buyerEmail,
          buyerName,
          buyerPhone,
          lang: lang // Pass user's language for email notifications
        };
        
        response = await axios.post(`${API}/payments/pass/guest`, requestData);
      }
      
      if (response.data.success && response.data.redirectUri) {
        window.location.href = response.data.redirectUri;
      } else {
        setError(t('passes.failedToInitPayment', 'Failed to initialize payment'));
      }
    } catch (error) {
      console.error('Failed to purchase pass:', error);
      setError(error.response?.data?.errorPl || error.response?.data?.error || 
        t('passes.purchaseError', 'Pass purchase error'));
    } finally {
      setPurchasing(false);
    }
  };

  // Get icon for group
  // Get translated group name
  const getTranslatedGroupName = (groupName) => {
    if (!groupName) return groupName;
    const key = 'passGroup_' + normalizeNameToKey(groupName);
    if (fitnetTranslations[key]?.[lang]) {
      return fitnetTranslations[key][lang];
    }
    return groupName;
  };

  // Get translated group description  
  const getTranslatedGroupDescription = (groupName) => {
    if (!groupName) return '';
    // First check for translated description from DB
    const descKey = 'passGroup_' + normalizeNameToKey(groupName) + '_desc';
    if (fitnetTranslations[descKey]?.[lang]) {
      return fitnetTranslations[descKey][lang];
    }
    // Fall back to hardcoded descriptions
    return getGroupDescription(groupName);
  };

  // Get icon/image for group from translations
  const getGroupIconImage = (groupName) => {
    if (!groupName) return null;
    const key = 'passGroup_' + normalizeNameToKey(groupName);
    return fitnetTranslations[key]?._imageUrl || null;
  };

  const getGroupIcon = (groupName) => {
    // First check if there's a custom icon from admin panel
    if (groupName) {
      const key = 'passGroup_' + normalizeNameToKey(groupName);
      const customIcon = fitnetTranslations[key]?._icon;
      if (customIcon) {
        const IconComponent = getIconByName(customIcon);
        if (IconComponent) return IconComponent;
      }
    }
    
    // Fallback to hardcoded icons based on name
    const name = (groupName || '').toLowerCase();
    if (name.includes('ilościow')) return Ticket;
    if (name.includes('okresow')) return Calendar;
    if (name.includes('sezonow')) return Gift;
    if (name.includes('szkółk')) return Award;
    return CreditCard;
  };

  // Get description for group (fallback)
  const getGroupDescription = (groupName) => {
    const name = groupName.toLowerCase();
    if (name.includes('ilościow')) {
      return t('passes.fixedEntries', 'Fixed number of entries');
    }
    if (name.includes('okresow')) {
      return t('passes.unlimitedForPeriod', 'Unlimited entries for period');
    }
    if (name.includes('sezonow')) {
      return t('passes.fullSeasonUnlimited', 'Full season unlimited');
    }
    if (name.includes('szkółk')) {
      return t('passes.schoolPasses', 'School passes');
    }
    return '';
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { 
      style: 'currency', 
      currency: 'PLN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(price);
  };

  // Show loading state while checking FitNet status
  if (fitnetLoading) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">{t('common.loading', 'Ładowanie...')}</p>
        </div>
      </div>
    );
  }

  // Show unavailable message when FitNet is down
  if (!isFitnetAvailable) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 md:px-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg"
        >
          <div className="glass-card p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/20 flex items-center justify-center">
              <ServerOff className="w-8 h-8 text-orange-400" />
            </div>
            <h1 className="text-2xl font-['Syne'] font-bold mb-4">{t('maintenance.passUnavailable', 'Zakup karnetów jest tymczasowo niedostępny')}</h1>
            <p className="text-muted-foreground mb-6">
              {t('maintenance.description', 'System rezerwacji jest chwilowo wyłączony. Przepraszamy za utrudnienia.')}
            </p>
            <Link 
              to={getHomePath(lang)} 
              className="btn-primary inline-block"
            >
              {t('maintenance.backToHome', 'Wróć do strony głównej')}
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('passes.loadingPasses', 'Loading passes...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-32 px-6 md:px-12">
        <div className="max-w-lg mx-auto text-center">
          <div className="p-6 bg-red-500/10 border border-red-500/20 rounded-xl">
            <p className="text-red-400">{error}</p>
            <button 
              onClick={fetchPassGroups}
              className="btn-primary mt-4"
            >
              {t('passes.tryAgain', 'Try again')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 md:px-12" data-testid="passes-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-['Syne'] font-bold mb-4">{t('passes.title', 'Karnety')}</h1>
          <p className="text-muted-foreground">
            {t('passes.saveWithPasses', 'Save money with our passes')}
          </p>
        </div>

        {/* Important Info Banner */}
        <div className="mb-10 p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/30" data-testid="activation-info-banner">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h3 className="font-bold text-emerald-400 mb-2">
                {t('passes.activationInfoTitle', 'Informacja o aktywacji karnetu')}
              </h3>
              <p className="text-sm text-foreground/80 leading-relaxed">
                {t('passes.activationInfoTextNew', 'Podczas zakupu możesz wybrać natychmiastową aktywację karnetu lub aktywować go później w swoim koncie. Masz {days} dni na aktywację od momentu zakupu.').replace('{days}', activationDeadlineDays)}
              </p>
            </div>
          </div>
        </div>

        {/* Category Selection */}
        <div className="mb-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {passGroups.map((group) => {
              const Icon = getGroupIcon(group.name);
              const iconImage = getGroupIconImage(group.name);
              const isActive = selectedGroup?.id === group.id;
              return (
                <button
                  key={group.id}
                  onClick={() => { setSelectedGroup(group); setSelectedPass(null); }}
                  className={`relative p-5 rounded-xl border-2 transition-all duration-300 text-left ${
                    isActive 
                      ? 'border-primary bg-primary/10 shadow-lg shadow-primary/20' 
                      : 'border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:border-gray-300 dark:hover:border-white/20'
                  }`}
                  data-testid={`pass-group-${group.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-colors overflow-hidden ${
                    isActive ? 'bg-primary/30' : 'bg-gray-100 dark:bg-white/10'
                  }`}>
                    {iconImage ? (
                      <img src={iconImage} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Icon className={`w-6 h-6 ${isActive ? 'text-primary' : 'text-gray-700 dark:text-white/70'}`} />
                    )}
                  </div>
                  <h3 className={`font-semibold mb-1 uppercase ${isActive ? 'text-primary' : 'text-gray-800 dark:text-white'}`}>
                    {getTranslatedGroupName(group.name)}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-muted-foreground">
                    {getTranslatedGroupDescription(group.name)}
                  </p>
                  <span className="absolute top-3 right-3 text-xs bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded-full text-gray-600 dark:text-white">
                    {group.passes.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Passes Grid */}
        {selectedGroup && (
          <motion.div
            key={selectedGroup.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-2xl font-bold mb-6">
              {selectedGroup.name}
            </h2>
            
            {selectedGroup.passes.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                {t('passes.noPassesInCategory', 'No passes available in this category')}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selectedGroup.passes.map((pass) => {
                  const isSelected = selectedPass?.id === pass.id;
                  return (
                    <motion.div
                      key={pass.id}
                      whileHover={{ scale: 1.02 }}
                      className={`relative glass-card p-6 cursor-pointer transition-all ${
                        isSelected 
                          ? 'ring-2 ring-primary shadow-lg shadow-primary/20' 
                          : 'hover:shadow-lg'
                      }`}
                      onClick={() => handleSelectPass(pass)}
                      data-testid={`pass-${pass.id}`}
                    >
                      {/* Selection indicator */}
                      {isSelected && (
                        <div className="absolute -top-3 -right-3 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      )}
                      
                      {/* Pass content */}
                      <div className="mb-4">
                        <h3 className="text-xl font-bold mb-2">{getTranslatedName(pass.name)}</h3>
                        
                        {/* Description */}
                        {pass.description && (
                          <p className="text-sm text-muted-foreground mb-3">
                            {getTranslatedDescription(pass.name, pass.description)}
                          </p>
                        )}
                        
                        {/* Features */}
                        <div className="space-y-2 text-sm text-muted-foreground">
                          {pass.entries && (
                            <div className="flex items-center gap-2">
                              <Ticket className="w-4 h-4 text-primary" />
                              <span>{pass.entries} {t('passes.entries', 'entries')}</span>
                            </div>
                          )}
                          {(pass.validMonths > 0 || pass.validDays > 0) && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-primary" />
                              <span>
                                {pass.validMonths > 0 
                                  ? `${pass.validMonths} ${pass.validMonths === 1 ? t('passes.month', 'month') : pass.validMonths < 5 ? t('passes.months24', 'months') : t('passes.months5plus', 'months')}`
                                  : `${pass.validDays} ${t('passes.days', 'days')}`
                                }
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Price */}
                      <div className="flex items-end justify-between pt-4 border-t border-white/10">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {t('passes.price', 'Price')}
                          </p>
                          <p className="text-2xl font-bold text-primary">
                            {formatPrice(pass.price)}
                          </p>
                          {/* Loyalty points badge */}
                          {loyaltyActive && getPassLoyaltyPoints(pass) > 0 && (
                            <div className="flex items-center gap-1 mt-1">
                              <Star className="w-3.5 h-3.5 text-yellow-500" />
                              <span className="text-xs text-yellow-500 font-medium">
                                +{getPassLoyaltyPoints(pass)} {t('loyalty.points', 'pkt')}
                              </span>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSelectPass(pass); }}
                          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                            isSelected 
                              ? 'bg-primary text-white' 
                              : 'bg-white/10 hover:bg-white/20'
                          }`}
                        >
                          {isSelected 
                            ? t('passes.selected', 'Selected')
                            : t('passes.select', 'Select')
                          }
                        </button>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* Purchase Section */}
        {selectedPass && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-10 glass-card p-6"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <h3 className="text-lg font-bold mb-1">
                  {t('passes.selectedPass', 'Selected pass')}
                </h3>
                <p className="text-xl text-primary font-semibold">{getTranslatedName(selectedPass.name)}</p>
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-muted-foreground">
                    {formatPrice(selectedPass.price)}
                  </p>
                  {/* Loyalty points badge */}
                  {loyaltyActive && getPassLoyaltyPoints(selectedPass) > 0 && (
                    <span className="inline-flex items-center gap-1 bg-yellow-500/10 px-2 py-0.5 rounded-full">
                      <Star className="w-3.5 h-3.5 text-yellow-500" />
                      <span className="text-xs text-yellow-500 font-medium">
                        +{getPassLoyaltyPoints(selectedPass)} {t('loyalty.points', 'pkt')}
                      </span>
                    </span>
                  )}
                </div>
              </div>
              
              {/* Buttons - different for guests vs authenticated */}
              {isAuthenticated ? (
                <button
                  onClick={openPurchaseModal}
                  className="btn-primary flex items-center gap-2"
                  data-testid="purchase-pass-button"
                >
                  <CreditCard className="w-5 h-5" />
                  {t('passes.buy', 'Buy pass')}
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => navigate('/login', { state: { from: '/passes' } })}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="login-to-buy-button"
                  >
                    <User className="w-5 h-5" />
                    {t('passes.loginToBuy', 'Login to buy')}
                  </button>
                  <button
                    onClick={openPurchaseModal}
                    className="btn-primary flex items-center gap-2"
                    data-testid="buy-as-gift-button"
                  >
                    <Gift className="w-5 h-5" />
                    {t('passes.buyAsGift', 'Buy as gift')}
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        )}
        
        {/* Purchase Modal */}
        {showPurchaseModal && selectedPass && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-2xl font-bold mb-6">
                {t('passes.purchaseOptions', 'Purchase options')}
              </h2>
              
              {/* Selected pass info */}
              <div className="bg-black/5 dark:bg-white/5 rounded-xl p-4 mb-6">
                <p className="font-semibold text-lg">{getTranslatedName(selectedPass.name)}</p>
                <p className="text-2xl font-bold text-primary">{formatPrice(selectedPass.price)}</p>
                {/* Loyalty points badge in modal */}
                {loyaltyActive && getPassLoyaltyPoints(selectedPass) > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 bg-yellow-500/10 px-3 py-1.5 rounded-full w-fit">
                    <Star className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-yellow-500 font-medium">
                      +{getPassLoyaltyPoints(selectedPass)} {t('loyalty.pointsEarned', 'punktów lojalnościowych')}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Error */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-500 p-3 rounded-lg mb-6">
                  {error}
                </div>
              )}
              
              {/* Purchase mode selection - only for authenticated users */}
              {isAuthenticated ? (
                <div className="space-y-3 mb-6">
                  <p className="font-medium mb-2">{t('passes.forWhom', 'For whom?')}</p>
                  
                  {/* Self */}
                  <label className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-colors ${
                    purchaseMode === 'self' ? 'bg-primary/20 border-2 border-primary' : 'bg-black/5 dark:bg-white/5 border-2 border-transparent'
                  }`}>
                    <input
                      type="radio"
                      name="purchaseMode"
                      checked={purchaseMode === 'self'}
                      onChange={() => setPurchaseMode('self')}
                      className="sr-only"
                    />
                    <User className="w-5 h-5 text-primary" />
                    <span>{t('passes.forMyself', 'For myself')}</span>
                    {purchaseMode === 'self' && <Check className="w-5 h-5 ml-auto text-primary" />}
                  </label>
                  
                  {/* Subaccount */}
                  {subaccounts.length > 0 && (
                    <label className={`mt-2 flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-colors ${
                      purchaseMode === 'subaccount' ? 'bg-primary/20 border-2 border-primary' : 'bg-black/5 dark:bg-white/5 border-2 border-transparent'
                    }`}>
                      <input
                        type="radio"
                        name="purchaseMode"
                        checked={purchaseMode === 'subaccount'}
                        onChange={() => setPurchaseMode('subaccount')}
                        className="sr-only"
                      />
                      <Users className="w-5 h-5 text-primary" />
                      <span>{t('passes.forOther', 'For someone in my account')}</span>
                      {purchaseMode === 'subaccount' && <Check className="w-5 h-5 ml-auto text-primary" />}
                    </label>
                  )}
                  
                  {/* Gift */}
                  <label className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-colors ${
                    purchaseMode === 'gift' ? 'bg-primary/20 border-2 border-primary' : 'bg-black/5 dark:bg-white/5 border-2 border-transparent'
                  }`}>
                    <input
                      type="radio"
                      name="purchaseMode"
                      checked={purchaseMode === 'gift'}
                      onChange={() => setPurchaseMode('gift')}
                      className="sr-only"
                    />
                    <Gift className="w-5 h-5 text-primary" />
                    <span>{t('passes.asGift', 'As a gift')}</span>
                    {purchaseMode === 'gift' && <Check className="w-5 h-5 ml-auto text-primary" />}
                  </label>
                </div>
              ) : (
                /* Guest mode - only gift available */
                <div className="mb-6">
                  <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 flex items-center gap-3">
                    <Gift className="w-6 h-6 text-primary" />
                    <div>
                      <p className="font-medium">{t('passes.giftPurchase', 'Gift purchase')}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('passes.giftPurchaseInfo', 'Recipient will receive UID code to activate the pass')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Subaccount selection */}
              {purchaseMode === 'subaccount' && (
                <div className="mb-6 space-y-2">
                  <Label>{t('passes.selectPerson', 'Select person')}</Label>
                  <div className="space-y-2">
                    {subaccounts.map(sub => (
                      <label
                        key={sub.subaccount_id}
                        className={`mb-2 flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedPerson === sub.subaccount_id 
                            ? 'bg-primary/20 border border-primary' 
                            : 'bg-black/5 dark:bg-white/5 border border-transparent'
                        }`}
                      >
                        <input
                          type="radio"
                          name="selectedPerson"
                          checked={selectedPerson === sub.subaccount_id}
                          onChange={() => setSelectedPerson(sub.subaccount_id)}
                          className="sr-only"
                        />
                        <span>{sub.firstName} {sub.lastName}</span>
                        {selectedPerson === sub.subaccount_id && <Check className="w-4 h-4 ml-auto text-primary" />}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Gift email inputs */}
              {purchaseMode === 'gift' && (
                <div className="mb-6 space-y-4">
                  <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-600 dark:text-yellow-400 p-3 rounded-lg text-sm">
                    <Gift className="w-4 h-4 inline mr-2" />
                    {t('passes.selectPersonInfo', 'Recipient will receive a UID code to activate the pass.')}
                  </div>
                  
                  {/* Buyer info for guests */}
                  {!isAuthenticated && (
                    <>
                      <div className="space-y-2">
                        <Label>{t('passes.buyerEmail', 'Your email (buyer)')}</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="email"
                            value={buyerEmail}
                            onChange={(e) => setBuyerEmail(e.target.value)}
                            placeholder="twoj@email.pl"
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('passes.buyerName', 'Your name')}</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="text"
                            value={buyerName}
                            onChange={(e) => setBuyerName(e.target.value)}
                            placeholder="Jan Kowalski"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('passes.buyerPhone', 'Your phone number')}</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                          <Input
                            type="tel"
                            value={buyerPhone}
                            onChange={(e) => setBuyerPhone(e.target.value)}
                            placeholder="+48 500 000 000"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      <hr className="border-white/10" />
                    </>
                  )}
                  
                  <div className="space-y-2">
                    <Label>{t('passes.recipientEmail', 'Gift recipient email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="email"
                        value={giftEmail}
                        onChange={(e) => setGiftEmail(e.target.value)}
                        placeholder="email@example.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('passes.confirmRecipientEmail', 'Confirm recipient email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="email"
                        value={giftEmailConfirm}
                        onChange={(e) => setGiftEmailConfirm(e.target.value)}
                        placeholder="email@example.com"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  {giftEmail && giftEmailConfirm && giftEmail.toLowerCase() === giftEmailConfirm.toLowerCase() && (
                    <p className="text-green-500 text-sm flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      {t('passes.emailsMatch', 'Emails match')}
                    </p>
                  )}
                </div>
              )}
              
              {/* Discount Code Section - only for authenticated non-gift purchases */}
              {isAuthenticated && purchaseMode !== 'gift' && (
                <div className="mb-6" data-testid="discount-code-section">
                  <Label className="mb-2 block">{t('passes.promoCodeOptional', 'Discount code (optional)')}</Label>
                  <div className="flex gap-2">
                    <div className="flex items-center flex-1 rounded-md border border-input bg-transparent ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                      <span className="px-3 text-muted-foreground">
                        <Tag className="w-4 h-4" />
                      </span>
                      <input
                        type="text"
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value.toUpperCase());
                          setPromoCodeValid(false);
                          setPromoCodeError(null);
                          setAppliedDiscount(null);
                        }}
                        placeholder={t('passes.enterCode', 'Enter code')}
                        className="flex-1 h-10 bg-transparent text-sm uppercase outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                        disabled={promoCodeValid}
                        data-testid="promo-code-input"
                      />
                      {promoCodeValid && (
                        <button
                          onClick={clearPromoCode}
                          className="px-3 text-muted-foreground hover:text-foreground"
                          data-testid="clear-promo-code"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {!promoCodeValid && (
                      <button
                        onClick={validatePromoCode}
                        disabled={promoCodeLoading || !promoCode.trim()}
                        className="btn-secondary px-4 flex items-center gap-2 disabled:opacity-50"
                        data-testid="validate-promo-code-btn"
                      >
                        {promoCodeLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          t('passes.apply', 'Apply')
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Promo code error */}
                  {promoCodeError && (
                    <p className="text-red-500 text-sm mt-2 flex items-center gap-1" data-testid="promo-code-error">
                      <X className="w-4 h-4" />
                      {promoCodeError}
                    </p>
                  )}
                  
                  {/* Promo code success */}
                  {promoCodeValid && appliedDiscount && (
                    <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg" data-testid="promo-code-success">
                      <p className="text-green-500 text-sm flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        {t('passes.codeApplied', 'Code applied!')}
                        <span className="font-semibold">
                          {appliedDiscount.discountType === 'percentage' 
                            ? `-${appliedDiscount.discountValue}%`
                            : `-${formatPrice(appliedDiscount.discountValue)}`
                          }
                        </span>
                      </p>
                      {calculateDiscountedPrice() === 0 && (
                        <p className="text-green-400 text-xs mt-1">
                          {t('passes.fullDiscountInfo', '100% discount - pass will be activated without payment!')}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Activation Option - shown for non-gift purchases (authenticated users only) */}
              {isAuthenticated && purchaseMode !== 'gift' && (
                <div className="mb-6" data-testid="activation-option-section">
                  <div 
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      activateImmediately 
                        ? 'bg-emerald-500/10 border-emerald-500/50' 
                        : 'bg-card border-input hover:border-input/70'
                    }`}
                    onClick={() => setActivateImmediately(!activateImmediately)}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <div className="mt-1">
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                          activateImmediately 
                            ? 'bg-emerald-500 border-emerald-500' 
                            : 'border-muted-foreground/40'
                        }`}>
                          {activateImmediately && <Check className="w-3.5 h-3.5 text-white" />}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className={`font-semibold mb-1 ${activateImmediately ? 'text-emerald-400' : 'text-foreground'}`}>
                          {t('passes.activateImmediately', 'Aktywuj karnet natychmiast')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {activateImmediately 
                            ? t('passes.activateImmediatelyDesc', 'Karnet zostanie aktywowany zaraz po opłaceniu. Ważność zacznie się od razu.')
                            : t('passes.activateLaterDesc', 'Karnet będzie czekał na aktywację w Twoim koncie. Masz {days} dni na aktywację.').replace('{days}', activationDeadlineDays)
                          }
                        </p>
                      </div>
                    </label>
                  </div>
                  
                  {!activateImmediately && (
                    <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                      <p className="text-sm text-amber-300 flex items-center gap-2">
                        <Info className="w-4 h-4 flex-shrink-0" />
                        {t('passes.pendingActivationNote', 'Karnet pojawi się w zakładce "Oczekujące na aktywację" w Twoim koncie.')}
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Action buttons */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowPurchaseModal(false);
                    setError(null);
                    clearPromoCode();
                  }}
                  className="btn-secondary flex-1"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handlePurchase}
                  disabled={
                    purchasing || 
                    (purchaseMode === 'subaccount' && !selectedPerson) || 
                    (purchaseMode === 'gift' && (!giftEmail || !giftEmailConfirm)) ||
                    (!isAuthenticated && purchaseMode === 'gift' && !buyerEmail)
                  }
                  className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="confirm-purchase-btn"
                >
                  {purchasing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t('passes.processing', 'Processing...')}
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      {calculateDiscountedPrice() === 0 ? (
                        t('passes.activateFreePass', 'Activate pass (free)')
                      ) : (
                        <>
                          {t('passes.pay', 'Pay')}{' '}
                          {appliedDiscount ? (
                            <>
                              <span className="line-through text-white/50">{formatPrice(selectedPass.price)}</span>
                              <span className="text-green-300 font-bold">{formatPrice(calculateDiscountedPrice())}</span>
                            </>
                          ) : (
                            formatPrice(selectedPass.price)
                          )}
                        </>
                      )}
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm">
            <Star className="w-4 h-4 text-primary" />
            <span>
              {t('passes.useAnytime', 'Use your pass anytime during the validity period')}
            </span>
          </div>
        </div>
      </div>
      
      {/* Success Modal for Free Pass */}
      <AnimatePresence>
        {showSuccessModal && successPassData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowSuccessModal(false);
              navigate('/account');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-card p-8 max-w-md w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center">
                <PartyPopper className="w-10 h-10 text-white" />
              </div>
              
              <h2 className="text-2xl font-bold mb-2">
                {t('passes.passActivated', 'Pass Activated!')}
              </h2>
              
              <p className="text-muted-foreground mb-6">
                {t('passes.passActivatedInfo', 'Your pass has been successfully activated with 100% discount.')}
              </p>
              
              <div className="bg-primary/20 rounded-xl p-4 mb-6">
                <p className="text-sm text-muted-foreground mb-1">
                  {t('passes.passCode', 'Pass Code')}
                </p>
                <p className="text-2xl font-mono font-bold tracking-wider text-primary">
                  {successPassData.passCode}
                </p>
              </div>
              
              <p className="text-sm text-muted-foreground mb-6">
                <strong>{successPassData.passName}</strong>
              </p>
              
              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/account');
                }}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                <Check className="w-5 h-5" />
                {t('passes.goToAccount', 'Go to Account')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PassesPage;
